import React from 'react';
import { adminService } from '../../services/adminService';
import { EmailReplyTemplate } from '../../types';

type Props = {
  templates: EmailReplyTemplate[];
  setTemplates: (templates: EmailReplyTemplate[]) => void;
};

type Scenario = EmailReplyTemplate['scenario'];
type Tone = EmailReplyTemplate['tone'];

type PreviewResult = {
  usedScenario: string;
  usedVariables: string[];
  missingVariables: string[];
  warnings: string[];
  renderedSubject: string;
  renderedBody: string;
};

const scenarioOptions: Scenario[] = [
  'ANSWER_AND_GUIDE',
  'ASK_MISSING_INFO',
  'ACKNOWLEDGE_AND_REVIEW',
  'SEND_OFFER',
  'FOLLOW_UP',
  'VERIFY_CLIENT',
  'CONFIRM_NEXT_STEP',
  'CUSTOM_REPLY',
];

const toneOptions: Tone[] = ['PROFESSIONAL', 'PREMIUM', 'FRIENDLY', 'URGENT', 'NEUTRAL'];

const variableExamples = [
  '{{clientName}}',
  '{{greetingLine}}',
  '{{missingFieldsText}}',
  '{{route}}',
  '{{pax}}',
  '{{date}}',
  '{{nextStep}}',
  '{{timeline}}',
];

const scenarioPresetContext: Record<Scenario, Record<string, string>> = {
  ANSWER_AND_GUIDE: {
    clientName: 'Client',
    route: 'ARK -> ZNZ',
    operatorName: 'Operations Team',
    companyName: 'Miracle Air',
    subjectBase: 'Charter Program',
  },
  ASK_MISSING_INFO: {
    clientName: 'Client',
    missingFieldsText: '- ROUTE\n- DATE\n- PAX',
    operatorName: 'Operations Team',
    companyName: 'Miracle Air',
    subjectBase: 'Flight Request',
  },
  ACKNOWLEDGE_AND_REVIEW: {
    clientName: 'Client',
    route: 'ARK -> ZNZ',
    pax: '4',
    date: '2026-05-01',
    time: '09:00',
    operatorName: 'Operations Team',
    companyName: 'Miracle Air',
    subjectBase: 'Flight Request',
  },
  SEND_OFFER: {
    clientName: 'Client',
    route: 'ARK -> ZNZ',
    pax: '4',
    date: '2026-05-01',
    time: '09:00',
    nextStep: 'Please confirm if you want us to issue the formal offer.',
    operatorName: 'Operations Team',
    companyName: 'Miracle Air',
    subjectBase: 'Offer',
  },
  FOLLOW_UP: {
    clientName: 'Client',
    route: 'ARK -> ZNZ',
    nextStep: 'Please confirm if we should proceed with this option.',
    operatorName: 'Operations Team',
    companyName: 'Miracle Air',
    subjectBase: 'Follow-up',
  },
  VERIFY_CLIENT: {
    clientName: 'Client',
    operatorName: 'Operations Team',
    companyName: 'Miracle Air',
    subjectBase: 'Verification',
  },
  CONFIRM_NEXT_STEP: {
    clientName: 'Client',
    nextStep: 'Please confirm your preferred departure window.',
    timeline: 'Within 30 minutes',
    operatorName: 'Operations Team',
    companyName: 'Miracle Air',
    subjectBase: 'Next Step',
  },
  CUSTOM_REPLY: {
    clientName: 'Client',
    nextStep: 'Please advise how you would like to proceed.',
    operatorName: 'Operations Team',
    companyName: 'Miracle Air',
    subjectBase: 'Message',
  },
};

const scenarioLabel = (value: Scenario) => value.replace(/_/g, ' ');

const createEmptyTemplate = (scenario: Scenario): EmailReplyTemplate => {
  const nowIso = new Date().toISOString();
  const id = `tpl_${scenario.toLowerCase()}_${Date.now()}`;
  const key = `${scenario}_SUPERVISOR_TEMPLATE`;
  return {
    id,
    key,
    scenario,
    title: scenarioLabel(scenario),
    tone: 'PROFESSIONAL',
    subjectTemplate: 'Re: {{subjectBase}}',
    bodyTemplate: '{{greetingLine}}\n\n{{nextStep}}',
    variables: ['greetingLine', 'nextStep', 'subjectBase'],
    helpText: '',
    active: true,
    updatedAt: nowIso,
  };
};

const stableSortById = (templates: EmailReplyTemplate[]) =>
  [...templates].sort((left, right) => String(left.id || '').localeCompare(String(right.id || '')));

export const ReplyTemplateManager: React.FC<Props> = ({ templates, setTemplates }) => {
  const [scenarioFilter, setScenarioFilter] = React.useState<'ALL' | Scenario>('ALL');
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<EmailReplyTemplate | null>(null);
  const [originalDraft, setOriginalDraft] = React.useState<EmailReplyTemplate | null>(null);
  const [previewJson, setPreviewJson] = React.useState<string>('{}');
  const [previewPresetScenario, setPreviewPresetScenario] = React.useState<Scenario>('ASK_MISSING_INFO');
  const [previewResult, setPreviewResult] = React.useState<PreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string>('');
  const [seedModalOpen, setSeedModalOpen] = React.useState(false);
  const [seedMode, setSeedMode] = React.useState<'missing_only' | 'replace_existing'>('missing_only');
  const [seedLoading, setSeedLoading] = React.useState(false);
  const [seedResultText, setSeedResultText] = React.useState('');

  const normalizedTemplates = React.useMemo(() => stableSortById(templates || []), [templates]);

  const activeByScenario = React.useMemo(() => {
    const map = new Map<Scenario, EmailReplyTemplate>();
    for (const scenario of scenarioOptions) {
      const match = normalizedTemplates.find(template => template.scenario === scenario && template.active);
      if (match) map.set(scenario, match);
    }
    return map;
  }, [normalizedTemplates]);

  const filteredTemplates = React.useMemo(() => {
    if (scenarioFilter === 'ALL') return normalizedTemplates;
    return normalizedTemplates.filter(template => template.scenario === scenarioFilter);
  }, [normalizedTemplates, scenarioFilter]);

  const openCreate = (scenario: Scenario) => {
    const next = createEmptyTemplate(scenario);
    setDraft(next);
    setOriginalDraft(null);
    setPreviewPresetScenario(scenario);
    setPreviewJson(JSON.stringify(scenarioPresetContext[scenario], null, 2));
    setPreviewResult(null);
    setSaveError('');
    setEditorOpen(true);
  };

  const openEdit = (template: EmailReplyTemplate) => {
    setDraft({ ...template, variables: [...(template.variables || [])] });
    setOriginalDraft({ ...template, variables: [...(template.variables || [])] });
    setPreviewPresetScenario(template.scenario);
    setPreviewJson(JSON.stringify(scenarioPresetContext[template.scenario], null, 2));
    setPreviewResult(null);
    setSaveError('');
    setEditorOpen(true);
  };

  const closeEditor = () => {
    const changed = JSON.stringify(draft || {}) !== JSON.stringify(originalDraft || {});
    if (changed) {
      const confirmed = window.confirm('Discard unsaved template changes?');
      if (!confirmed) return;
    }
    setEditorOpen(false);
    setDraft(null);
    setOriginalDraft(null);
    setPreviewResult(null);
    setSaveError('');
  };

  const saveDraft = () => {
    if (!draft) return;
    const requiredText = [
      ['id', draft.id],
      ['key', draft.key],
      ['title', draft.title],
      ['subjectTemplate', draft.subjectTemplate],
      ['bodyTemplate', draft.bodyTemplate],
    ] as const;
    for (const [label, value] of requiredText) {
      if (!String(value || '').trim()) {
        setSaveError(`${label} is required.`);
        return;
      }
    }

    const byId = new Map<string, EmailReplyTemplate>(normalizedTemplates.map(template => [template.id, template]));
    const exists = byId.has(draft.id);
    const immutableKey = exists ? String(byId.get(draft.id)?.key || '') : null;
    if (immutableKey && immutableKey !== draft.key) {
      setSaveError('Template key is immutable after creation.');
      return;
    }

    const duplicateKey = normalizedTemplates.find(template => template.id !== draft.id && template.key === draft.key);
    if (duplicateKey) {
      setSaveError(`Template key already exists: ${draft.key}`);
      return;
    }

    const duplicateId = !exists
      ? normalizedTemplates.find(template => template.id === draft.id)
      : null;
    if (duplicateId) {
      setSaveError(`Template id already exists: ${draft.id}`);
      return;
    }

    let nextTemplates = normalizedTemplates.some(template => template.id === draft.id)
      ? normalizedTemplates.map(template => (template.id === draft.id ? { ...draft, updatedAt: new Date().toISOString() } : template))
      : [...normalizedTemplates, { ...draft, updatedAt: new Date().toISOString() }];

    if (draft.active) {
      nextTemplates = nextTemplates.map(template =>
        template.scenario === draft.scenario
          ? { ...template, active: template.id === draft.id }
          : template
      );
    }

    setTemplates(stableSortById(nextTemplates));
    setEditorOpen(false);
    setDraft(null);
    setOriginalDraft(null);
    setPreviewResult(null);
    setSaveError('');
  };

  const toggleActive = (template: EmailReplyTemplate) => {
    const nextActive = !template.active;
    const nextTemplates = normalizedTemplates.map(item => {
      if (item.id === template.id) return { ...item, active: nextActive, updatedAt: new Date().toISOString() };
      if (nextActive && item.scenario === template.scenario) return { ...item, active: false, updatedAt: item.updatedAt };
      return item;
    });
    setTemplates(stableSortById(nextTemplates));
  };

  const runPreview = async () => {
    if (!draft) return;
    try {
      const parsedContext = JSON.parse(previewJson || '{}');
      setPreviewLoading(true);
      const result = await adminService.previewEmailReplyTemplate(draft, parsedContext);
      setPreviewResult(result);
    } catch (error: any) {
      setPreviewResult({
        usedScenario: draft.scenario,
        usedVariables: [],
        missingVariables: [],
        warnings: [error?.message || 'Invalid preview JSON context'],
        renderedSubject: '',
        renderedBody: '',
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const applyPreset = (scenario: Scenario) => {
    setPreviewPresetScenario(scenario);
    setPreviewJson(JSON.stringify(scenarioPresetContext[scenario], null, 2));
  };

  const runSeedTemplates = async () => {
    try {
      setSeedLoading(true);
      setSeedResultText('');
      const result = await adminService.seedEmailReplyTemplates(seedMode);
      const nextTemplates = stableSortById(Array.isArray(result.templates) ? result.templates : normalizedTemplates);
      setTemplates(nextTemplates);
      const scenarios = (result.scenariosAffected || []).join(', ') || 'None';
      setSeedResultText(`Seed complete. Created: ${result.createdCount}, Updated: ${result.updatedCount}. Scenarios: ${scenarios}`);
      setSeedModalOpen(false);
    } catch (error: any) {
      setSeedResultText(error?.response?.data?.error || error?.message || 'Failed to seed templates');
    } finally {
      setSeedLoading(false);
    }
  };

  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Reply Templates</p>
          <p className="mt-1 text-sm font-medium text-slate-500">
            Manage supervisor-approved reply templates used by the Email Reply Intelligence layer.
            AI may refine wording later, but template is always the starting structure.
            Do not include signature blocks in template body; operator signature is appended automatically at send time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={scenarioFilter}
            onChange={(event) => setScenarioFilter(event.target.value as 'ALL' | Scenario)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700"
          >
            <option value="ALL">All Scenarios</option>
            {scenarioOptions.map(option => (
              <option key={option} value={option}>{scenarioLabel(option)}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              setSeedMode('missing_only');
              setSeedModalOpen(true);
            }}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-[10px] font-black uppercase tracking-widest text-slate-700"
          >
            Seed Supervisor Templates
          </button>
          <button
            type="button"
            onClick={() => openCreate('ASK_MISSING_INFO')}
            className="rounded-xl bg-slate-900 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-white"
          >
            Create Template
          </button>
        </div>
      </div>

      {seedResultText ? (
        <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-800">
          {seedResultText}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-2">
        {filteredTemplates.map(template => (
          <div key={template.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-black text-slate-800">{template.title}</p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  {scenarioLabel(template.scenario)} · {template.tone}
                </p>
                <p className="text-[10px] font-semibold text-slate-400">Key: {template.key}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => toggleActive(template)}
                  className={`rounded-lg border px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest ${
                    template.active
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 bg-white text-slate-500'
                  }`}
                >
                  {template.active ? 'Active' : 'Inactive'}
                </button>
                <button
                  type="button"
                  onClick={() => openEdit(template)}
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-600"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const duplicate: EmailReplyTemplate = {
                      ...template,
                      id: `${template.id}_copy_${Date.now()}`,
                      key: `${template.key}_COPY_${Date.now()}`,
                      title: `${template.title} (Copy)`,
                      active: false,
                      updatedAt: new Date().toISOString(),
                    };
                    setTemplates(stableSortById([...normalizedTemplates, duplicate]));
                  }}
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-600"
                >
                  Duplicate
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">System Default Fallback</p>
        {scenarioOptions.map(scenario => {
          const active = activeByScenario.get(scenario);
          return (
            <div key={scenario} className="flex items-center justify-between text-[11px]">
              <span className="font-semibold text-slate-700">{scenarioLabel(scenario)}</span>
              <span className={`rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-widest ${
                active ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
              }`}>
                {active ? `Using ${active.title}` : 'System Default Fallback'}
              </span>
            </div>
          );
        })}
      </div>

      {editorOpen && draft ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/45 p-4">
          <div className="max-h-[92vh] w-full max-w-5xl overflow-auto rounded-2xl border border-slate-200 bg-white p-5">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h4 className="text-base font-black text-slate-900">Reply Template Editor</h4>
                <p className="text-[12px] text-slate-500">Configure scenario template, variables, and deterministic preview output.</p>
              </div>
              <button type="button" onClick={closeEditor} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-black uppercase tracking-widest text-slate-500">X</button>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <label className="space-y-1">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Template Title</span>
                <input value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              </label>
              <label className="space-y-1">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Key</span>
                <input
                  value={draft.key}
                  disabled={Boolean(originalDraft)}
                  onChange={(event) => setDraft({ ...draft, key: event.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-100"
                />
                {originalDraft ? <p className="text-[10px] font-semibold text-slate-400">Key is immutable after creation.</p> : null}
              </label>

              <label className="space-y-1">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Scenario</span>
                <select
                  value={draft.scenario}
                  onChange={(event) => setDraft({ ...draft, scenario: event.target.value as Scenario })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                >
                  {scenarioOptions.map(option => (
                    <option key={option} value={option}>{scenarioLabel(option)}</option>
                  ))}
                </select>
              </label>

              <label className="space-y-1">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Tone</span>
                <select
                  value={draft.tone}
                  onChange={(event) => setDraft({ ...draft, tone: event.target.value as Tone })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                >
                  {toneOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>

              <label className="space-y-1 lg:col-span-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Subject Template</span>
                <input value={draft.subjectTemplate} onChange={(event) => setDraft({ ...draft, subjectTemplate: event.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              </label>

              <label className="space-y-1 lg:col-span-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Body Template</span>
                <textarea value={draft.bodyTemplate} onChange={(event) => setDraft({ ...draft, bodyTemplate: event.target.value })} rows={8} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              </label>

              <label className="space-y-1 lg:col-span-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Variables (one per line)</span>
                <textarea
                  value={(draft.variables || []).join('\n')}
                  onChange={(event) => setDraft({ ...draft, variables: event.target.value.split('\n').map(v => v.trim()).filter(Boolean) })}
                  rows={3}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                />
              </label>

              <label className="space-y-1 lg:col-span-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Help Text / Guidance Notes</span>
                <textarea value={draft.helpText || ''} onChange={(event) => setDraft({ ...draft, helpText: event.target.value })} rows={3} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              </label>

              <div className="lg:col-span-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const nextActive = !draft.active;
                    if (nextActive) {
                      const clash = normalizedTemplates.find(template => template.id !== draft.id && template.scenario === draft.scenario && template.active);
                      if (clash) {
                        const confirmSwitch = window.confirm(`Activate this template and deactivate "${clash.title}" for ${scenarioLabel(draft.scenario)}?`);
                        if (!confirmSwitch) return;
                      }
                    }
                    setDraft({ ...draft, active: nextActive });
                  }}
                  className={`rounded-xl border px-3 py-2 text-[10px] font-black uppercase tracking-widest ${
                    draft.active ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500'
                  }`}
                >
                  {draft.active ? 'Active' : 'Inactive'}
                </button>
                <span className="text-[10px] text-slate-500">Deactivating template returns this scenario to system default fallback.</span>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Available Variables</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {variableExamples.map(example => (
                  <span key={example} className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-slate-600">{example}</span>
                ))}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Preview Context</p>
                  <select
                    value={previewPresetScenario}
                    onChange={(event) => applyPreset(event.target.value as Scenario)}
                    className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold"
                  >
                    {scenarioOptions.map(option => (
                      <option key={option} value={option}>{scenarioLabel(option)}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  value={previewJson}
                  onChange={(event) => setPreviewJson(event.target.value)}
                  rows={10}
                  className="w-full rounded-lg border border-slate-200 bg-white p-2 font-mono text-[11px]"
                />
                <button type="button" onClick={() => void runPreview()} className="rounded-lg bg-slate-900 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-white">
                  {previewLoading ? 'Rendering...' : 'Preview Output'}
                </button>
              </div>

              <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Preview Output</p>
                {!previewResult ? (
                  <p className="text-xs text-slate-400">Run preview to see rendered output.</p>
                ) : (
                  <>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Used Scenario</p>
                      <p className="text-xs font-semibold text-slate-700">{previewResult.usedScenario}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Used Variables</p>
                      <p className="text-xs text-slate-700">{previewResult.usedVariables.join(', ') || 'None'}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Rendered Subject</p>
                      <p className="text-xs font-semibold text-slate-800">{previewResult.renderedSubject || '(empty)'}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Rendered Body</p>
                      <pre className="whitespace-pre-wrap text-xs text-slate-700">{previewResult.renderedBody || '(empty)'}</pre>
                    </div>
                    {previewResult.missingVariables.length > 0 ? (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] font-semibold text-amber-700">
                        Missing Variables: {previewResult.missingVariables.join(', ')}
                      </div>
                    ) : null}
                    {previewResult.warnings.length > 0 ? (
                      <div className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-[11px] font-semibold text-rose-700">
                        {previewResult.warnings.map((warning, index) => (
                          <div key={`${warning}-${index}`}>{warning}</div>
                        ))}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </div>

            {saveError ? <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] font-semibold text-rose-700">{saveError}</div> : null}

            <div className="mt-4 flex items-center justify-end gap-2">
              <button type="button" onClick={closeEditor} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600">Cancel</button>
              <button type="button" onClick={saveDraft} className="rounded-lg bg-slate-900 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-white">Save Template</button>
            </div>
          </div>
        </div>
      ) : null}

      {seedModalOpen ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/45 p-4">
          <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-5">
            <h4 className="text-base font-black text-slate-900">Seed Supervisor Templates</h4>
            <p className="mt-1 text-sm text-slate-600">
              These templates are used by Email Reply Intelligence as the starting structure. Signatures are not included because the selected signature is appended automatically at send time.
            </p>
            <div className="mt-4 space-y-2">
              <label className="flex items-start gap-2 rounded-xl border border-slate-200 p-3">
                <input
                  type="radio"
                  name="seed-mode"
                  checked={seedMode === 'missing_only'}
                  onChange={() => setSeedMode('missing_only')}
                  className="mt-0.5"
                />
                <span>
                  <span className="block text-sm font-bold text-slate-800">Create missing only</span>
                  <span className="block text-xs text-slate-500">Create templates only for scenarios without an active admin template.</span>
                </span>
              </label>
              <label className="flex items-start gap-2 rounded-xl border border-slate-200 p-3">
                <input
                  type="radio"
                  name="seed-mode"
                  checked={seedMode === 'replace_existing'}
                  onChange={() => setSeedMode('replace_existing')}
                  className="mt-0.5"
                />
                <span>
                  <span className="block text-sm font-bold text-slate-800">Replace all scenario templates</span>
                  <span className="block text-xs text-slate-500">Update seeded templates across all scenarios and set them as active.</span>
                </span>
              </label>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setSeedModalOpen(false)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-black uppercase tracking-widest text-slate-600"
                disabled={seedLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={runSeedTemplates}
                className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-black uppercase tracking-widest text-white disabled:opacity-50"
                disabled={seedLoading}
              >
                {seedLoading ? 'Seeding…' : 'Run Seed'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
